import { useParams, useNavigate } from 'react-router';
import Layout from '../mui-components/Layout';
import { pageTitles } from '../data/pageTitles';
import { useGlobalContext } from '../context/GlobalStore';
import { Typography, Grid, Paper, List, ListItem, Divider, Button, Box, Link, Snackbar, Alert } from '@mui/material';
import { useState } from 'react';
import apiClient from '../utils/apiClient';
import AlertModal from '../mui-components/AlertModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import LoadingDialog from '../mui-components/LoadingDialog';
import Loading from '../mui-components/Loading';

export default function Order() {
  document.title = pageTitles.order;
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, setOrders } = useGlobalContext();
  const [expandedImeis, setExpandedImeis] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleCancelOrderClick = () => {
    setOpenCancelConfirm(true);
  };

  const handleCopyOrderClick = () => {
    if (!order) return;

    const orderToCopy = {
      // Recipient Info
      fname: order.first_name_recipient,
      lname: order.last_name_recipient,
      phone: order.phone_recipient,
      email: order.email_recipient,
      company: order.company_recipient,

      // Shipping Info
      address1: order.address1,
      address2: order.address2,
      city: order.city,
      state: order.state,
      pcode: order.postal_code,
      ccode: order.country_code,

      // Pass all products in a single array
      products: order.devices ? order.devices.map(d => ({ sku: d.sku, quantity: d.quantity })) : [],
    };

    navigate('/placeOrder', { state: { copiedOrder: orderToCopy } });
  };

  const cancelOrder = async () => {
    setOpenCancelConfirm(false);
    setIsLoading(true);
    setError(null);

    try {
      const accountId = state.account.pylo_accounts.find(
        (a) => a.api_key === state.apiKey.key
      )['id'];
      const response = await apiClient.post('/placeOrder', {
         order: { order_number: order.order_number }, 
         action: 'cancel_order',
         account_id: accountId,
      });

      if (response && response.status === 'OK') {
        const newOrders = state.orders.map((o) =>
          o.order_number === response['cancelled order number']
            ? { ...o, order_status: 'cancelled' }
            : o
        );
        setOrders(newOrders);
        setSnackbarMessage(`Order ${response['cancelled order number']} has been successfully cancelled.`);
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred while cancelling the order.');
      setOpenErrorAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const carrierTrackingUrls = {
    fedex: (trackingNumber) => `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  };

  // Find the order
  const order = state.orders.find((o) => o.order_number === id);

  // Get product offerings for device descriptions
  let productOfferings = {};
  if (state.account && state.account.pylo_accounts && state.apiKey && state.apiKey.key) {
    const currentPyloAccount = state.account.pylo_accounts.find(
      (acc) => acc.api_key === state.apiKey.key
    );

    if (currentPyloAccount) {
      if (typeof currentPyloAccount.product_offerings === 'object' && 
          currentPyloAccount.product_offerings !== null && 
          !Array.isArray(currentPyloAccount.product_offerings)) {
        productOfferings = currentPyloAccount.product_offerings;
      }
    }
  }

  const getProductDescription = (sku) => {
    const product = productOfferings[sku];
    return product && product.name ? product.name : sku;
  };

  if (state.isDeviceLoading) {
    return (
      <Layout title="Loading Order Details...">
        <Loading />
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout title="Order Not Found">
        <Typography>The requested order could not be found.</Typography>
      </Layout>
    );
  }

  // Helper Component to display a field if it has a value
  const DetailItem = ({ label, value }) =>
    value || value === 0 || value === '' ? (
      <Grid item xs={12} sm={6} container component="div" alignItems="baseline"> 
        <Grid item xs={12} sm="auto">
          <Typography variant="subtitle1" component="dt" sx={{ fontWeight: 'bold', mr: { sm: 0.5 }, mb: { xs: 0.5, sm: 0 } }}>{label}:</Typography>
        </Grid>
        <Grid item xs={12} sm>
          <Typography variant="body1" component="dd" sx={{ wordBreak: 'break-word' }}>{String(value)}</Typography>
        </Grid>
      </Grid>
    ) : null;

  return (
    <Layout title={`Order Details: ${order.order_number}`}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        severity="success" variant="filled"
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCopyOrderClick}
        >
          Copy Order
        </Button>
        {order.order_status === 'awaiting_shipment' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelOrderClick}
          >
            Cancel Order
          </Button>
        )}
      </Box>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={1} component="dl">
          {/* Order Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom component="div">Order Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <DetailItem label="Order Number" value={order.order_number} />
          <DetailItem label="Order Status" value={order.order_status} />
          <DetailItem label="Date Placed" value={order.date_placed} />
          <DetailItem label="Date Processed" value={order.date_order_processed} />
          <DetailItem label="Patient ID" value={order.patient_id} />
          <DetailItem label="Practice ID" value={order.practice_id} />
          <DetailItem label="Order Notes" value={order.order_notes_from_buyer} />

          {/* Recipient Information Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom component="div">Recipient Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <DetailItem label="Company" value={order.company_recipient} />
          <DetailItem label="Email" value={order.email_recipient} />
          <DetailItem label="First Name" value={order.first_name_recipient} />
          <DetailItem label="Last Name" value={order.last_name_recipient} />
          <DetailItem label="Phone" value={order.phone_recipient} />

          {/* Shipping Information Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom component="div">Shipping Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <DetailItem label="Address Line 1" value={order.address1} />
          <DetailItem label="Address Line 2" value={order.address2} />
          <DetailItem label="Address Line 3" value={order.address3} />
          <DetailItem label="City" value={order.city} />
          <DetailItem label="State" value={order.state} />
          <DetailItem label="Postal Code" value={order.postal_code} />
          <DetailItem label="Country Code" value={order.country_code} />
          <DetailItem label="Requested Shipping Service" value={order.order_requested_shipping_service} />
          <DetailItem label="Confirmation" value={order.confirmation} />
          <DetailItem label="Signature Required" value={order.require_signature} />
          <DetailItem label="Ship Date" value={order.ship_date} />
          <DetailItem label="Carrier" value={order.carrier_code} />
          {/* Tracking Number with conditional link */}
          {order.tracking_number && (() => {
            const carrier = order.carrier_code ? order.carrier_code.toLowerCase() : null;
            const trackingUrlFn = carrier && carrierTrackingUrls[carrier];

            if (trackingUrlFn) {
              const trackingUrl = trackingUrlFn(order.tracking_number);
              return (
                <Grid item xs={12} sm={6} container component="div" alignItems="baseline">
                  <Grid item xs={12} sm="auto">
                    <Typography variant="subtitle1" component="dt" sx={{ fontWeight: 'bold', mr: { sm: 0.5 }, mb: { xs: 0.5, sm: 0 } }}>Tracking Number:</Typography>
                  </Grid>
                  <Grid item xs={12} sm>
                    <Link href={trackingUrl} target="_blank" rel="noopener noreferrer" sx={{ verticalAlign: 'baseline' }}>
                      <Typography variant="body1" component="span" sx={{ wordBreak: 'break-word' }}>
                        {order.tracking_number}
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
              );
            } else {
              // Fallback to DetailItem if no specific link logic for the carrier or carrier_code is missing
              return <DetailItem label="Tracking Number" value={order.tracking_number} />;
            }
          })()}

          {/* Devices Section */}
          {order.devices && order.devices.length > 0 && (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom component="div">Devices</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <List dense sx={{ width: '100%' }}>
                  {order.devices.map((device, index) => {
                    const isImeiExpanded = expandedImeis[index] || false;
                    const toggleImeiExpansion = () => {
                      setExpandedImeis(prev => ({ ...prev, [index]: !prev[index] }));
                    };

                    const imeiDisplayLimit = 3;
                    const hasManyImeis = device.imeis && device.imeis.length > imeiDisplayLimit;
                    const imeisToShow = device.imeis ? (isImeiExpanded ? device.imeis : device.imeis.slice(0, imeiDisplayLimit)) : [];

                    return (
                      <ListItem key={index} disableGutters sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="subtitle1" component="div">
                          <Typography component="span" sx={{ fontWeight: 'bold' }}>{device.sku}:</Typography>{` ${getProductDescription(device.sku)}`}
                        </Typography>
                        <Typography variant="body2" sx={{ pl: 2, mt: 0.5 }}>{`Quantity: ${device.quantity}`}</Typography>
                        {device.imeis && device.imeis.length > 0 && (
                          <Box sx={{ pl: 2, mt: 0.25 }}>
                            <Typography variant="body2" component="div" sx={{ fontWeight: 'medium', mb: 0.5 }}>IMEIs:</Typography>
                            {imeisToShow.map((imei, imeiIndex) => (
                              <Typography key={imeiIndex} variant="body2" component="div" sx={{ pl: 2, wordBreak: 'break-all', mb: 0.25 }}>
                                {imei}
                              </Typography>
                            ))}
                            {hasManyImeis && !isImeiExpanded && (
                              <Typography variant="caption" sx={{ pl: 2, display: 'block', mt: 0.25 }}>
                                ... and {device.imeis.length - imeiDisplayLimit} more
                              </Typography>
                            )}
                            {hasManyImeis && (
                              <Button size="small" onClick={toggleImeiExpansion} sx={{ pl: 2, pt:0, pb:0, textTransform: 'none', alignSelf: 'flex-start', mt: 0.5 }}>
                                {isImeiExpanded ? 'Show Less' : `Show More (+${device.imeis.length - imeiDisplayLimit})`}
                              </Button>
                            )}
                          </Box>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      <LoadingDialog open={isLoading} />
      <ErrorAlertModal
        open={openErrorAlert}
        handleClose={() => setOpenErrorAlert(false)}
        title="Error Cancelling Order"
        message={error}
      />
      <AlertModal
        open={openCancelConfirm}
        handleClose={() => setOpenCancelConfirm(false)}
        onConfirm={cancelOrder}
        title="Confirm Order Cancellation"
        message={`Are you sure you want to cancel order ${order.order_number}? This action cannot be undone.`}
      />
    </Layout>
  );
}
